import * as Api from '@ViewModels';
import { events } from 'fetch-event-stream';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import { SALES_LIVE_COACHING_ASSISTANT_NAME } from '../../../constants/coachingContract';
import { buildCheckRequest, buildIdFrom } from './labLogic';
import { parseResponseEvent } from '../../../focused-view/ai/useCoachConversationStream';

const PREVIEW_POLL_MS = 5000;
const PREVIEW_TIMEOUT_MS = 8 * 60 * 1000;
const BUILD_CHECK_TIMEOUT_MS = 20000;
const STREAM_IDLE_MS = 90 * 1000;
const BUILD_ID_REUSE_MS = 10 * 60 * 1000;
const buildIds = new Map<string, { at: number; id: Promise<string> }>();

const throwIfAborted = (signal: AbortSignal) => {
	if (signal.aborted) throw new DOMException('The Coach Lab call was stopped', 'AbortError');
};

/**
 * The calls one Coach Lab session makes. A call is a scripted sequence (open two conversations, hand over the plan, then
 * alternate coach and prospect turns as the manager types), so these are awaitable steps the call drives in order rather
 * than queries a component renders from. Every step takes the call's signal and throws once it is aborted, so a stopped
 * call never returns a partial reply.
 */
export const useCoachLabTransport = () => {
	const userSession = useUserSession();
	const { logApiError } = useEventLogging('CoachLab');
	const helper = userSession.webServiceHelper;
	const url = (urlPath: string) => Api.ImpersonationBroker.composeApiUrl({ urlPath });

	const createConversation = async (agentTemplateId: string, signal: AbortSignal, companyId?: string) => {
		const params = new URLSearchParams({ agentTemplateId, source: 'coach-lab' });
		if (companyId) params.append('companyId', companyId);
		const conversation = await helper.callAsync<Api.AIConversation>({
			method: 'POST',
			path: url(`ai/assistant/${SALES_LIVE_COACHING_ASSISTANT_NAME}/conversation?${params.toString()}`),
			signal,
		});
		throwIfAborted(signal);
		if (!conversation?.id) throw new Error('No conversation was created');
		return conversation.id;
	};

	/**
	 * Sends one message and resolves with the assistant's full reply; `onText` sees it grow as it streams. A reply that
	 * goes quiet (no event, not even a heartbeat) for STREAM_IDLE_MS fails instead of leaving the call waiting forever.
	 */
	const send = async (conversationId: string, text: string, signal: AbortSignal, onText?: (soFar: string) => void) => {
		const body: Api.AIMessageRequest = { chatMessages: [{ id: null, role: Api.AIChatRole.User, text }] };
		const idle = new AbortController();
		let idleTimer = setTimeout(() => idle.abort(), STREAM_IDLE_MS);
		const stillAlive = () => {
			clearTimeout(idleTimer);
			idleTimer = setTimeout(() => idle.abort(), STREAM_IDLE_MS);
		};
		const streamSignal = AbortSignal.any([signal, idle.signal]);
		let reply: string | undefined;
		let failure: unknown;
		try {
			reply = await readReply(conversationId, body, streamSignal, stillAlive, onText);
		} catch (error) {
			failure = error;
		}
		clearTimeout(idleTimer);
		throwIfAborted(signal);
		if (idle.signal.aborted) throw new Error('The assistant stopped responding');
		if (failure !== undefined || reply === undefined) throw failure;
		return reply;
	};

	const readReply = async (
		conversationId: string,
		body: Api.AIMessageRequest,
		signal: AbortSignal,
		stillAlive: () => void,
		onText?: (soFar: string) => void
	) => {
		const response = await new Promise<Response>((resolve, reject) =>
			helper.callWebServiceRawResponse(
				url(`ai/assistant/${SALES_LIVE_COACHING_ASSISTANT_NAME}/conversation/${conversationId}/stream`),
				'POST',
				body,
				resolve,
				reject,
				undefined,
				true,
				signal
			)
		);
		const consume = async () => {
			let reply = '';
			for await (const event of events(response, signal)) {
				stillAlive();
				if (event.event === 'Error') throw new Error('The assistant stream failed');
				if (event.event !== 'Response' || !event.data) continue;
				const parsed = parseResponseEvent(event.data);
				if (!parsed) continue;
				reply = parsed.isDelta ? reply + parsed.text : parsed.text;
				onText?.(reply);
			}
			// events() ends quietly on abort, which would otherwise hand back a cut-off reply as if it were whole.
			throwIfAborted(signal);
			return reply.trim();
		};
		// The stream only notices an abort when its next chunk arrives, so a silent stream is raced against the signal.
		const stopped = new Promise<never>((_, reject) => {
			const stop = () => reject(new DOMException('The Coach Lab call was stopped', 'AbortError'));
			if (signal.aborted) stop();
			else signal.addEventListener('abort', stop, { once: true });
		});
		return Promise.race([consume(), stopped]);
	};

	const leadContext = async (companyId: string, signal: AbortSignal) => {
		const context = await helper.callAsync<string>({
			method: 'GET',
			path: url(`lead/${encodeURIComponent(companyId)}/AIContext`),
			signal,
		});
		throwIfAborted(signal);
		return context ?? undefined;
	};

	const phoneCall = async (phoneCallId: string, signal: AbortSignal) => {
		const call = await helper.callAsync<{ companyId?: string; aiConversationId?: string }>({
			method: 'GET',
			path: url(`phonecall/${encodeURIComponent(phoneCallId)}`),
			signal,
		});
		throwIfAborted(signal);
		return call;
	};

	const companyForCall = async (phoneCallId: string, signal: AbortSignal) =>
		(await phoneCall(phoneCallId, signal))?.companyId;

	/** Writes the lead's real Game Plan through the preview job, which can take several minutes. Null when it fails. */
	const previewGamePlan = async (companyId: string, signal: AbortSignal) => {
		type PreviewRun = { id: string; status: string; plan?: string };
		const base = `lead/${encodeURIComponent(companyId)}/salesGamePlan/preview`;
		throwIfAborted(signal);
		try {
			let run = await helper.callAsync<PreviewRun>({ method: 'POST', path: url(base), signal });
			const started = Date.now();
			while (run && run.status !== 'Ready' && run.status !== 'Failed' && Date.now() - started < PREVIEW_TIMEOUT_MS) {
				await new Promise(resolve => setTimeout(resolve, PREVIEW_POLL_MS));
				throwIfAborted(signal);
				run = await helper.callAsync<PreviewRun>({ method: 'GET', path: url(`${base}/${run.id}`), signal });
			}
			return run?.status === 'Ready' && run.plan ? run.plan : null;
		} catch (error) {
			throwIfAborted(signal);
			// No plan is a supported state: the coach then works from the lead record, as the page tells the manager.
			logApiError('PreviewGamePlan-Error', error as Api.IOperationResultNoValue);
			return null;
		}
	};

	/**
	 * Which version of a coach template this is: coach templates answer the build-check sentinel with their build id. On its
	 * own short timeout, so it can neither outlive the page nor hold up a save; empty when it does not answer. A sitting
	 * saves many calls on one coach, so the answer is reused for a few minutes and asked for when the call starts.
	 */
	const buildId = (coachTemplateId: string) => {
		const cached = buildIds.get(coachTemplateId);
		if (cached && Date.now() - cached.at < BUILD_ID_REUSE_MS) return cached.id;
		const signal = AbortSignal.timeout(BUILD_CHECK_TIMEOUT_MS);
		const id = createConversation(coachTemplateId, signal)
			.then(conversationId => send(conversationId, buildCheckRequest(), signal))
			.then(buildIdFrom)
			.catch(() => '')
			.then(found => {
				if (!found) buildIds.delete(coachTemplateId);
				return found;
			});
		buildIds.set(coachTemplateId, { at: Date.now(), id });
		return id;
	};

	return { buildId, companyForCall, createConversation, leadContext, phoneCall, previewGamePlan, send };
};
